import { useFlag } from '@calendly/feature-check-react';
import { HtmlParser } from '@calendly/html-parser';
import { buildQuery } from '@calendly/http-tools';
import { I18n } from '@calendly/i18n';
import { Button } from '@calendly/ui/components/button';
import { Popup, Header, Title, Body, Footer } from '@calendly/ui/components/popup';
import classNames from 'classnames';
import { useState, useEffect, forwardRef } from 'react';
import { useSelector } from 'react-redux';
import { featureTierSelector, isOnTrialStageSelector, upgradeInProgressSelector, isAvailableFeatureSelector } from 'features/billing';
import { useTrack, TRACKING_EVENT_NAMES } from 'hooks/analytics_events';
import { shouldBlockTrialSMSForPublisher } from 'services/workflows/templates';
import { appNewWorkflowUrl } from 'services/workflows/url';
import { UpgradeLink } from '../../upgrade_link';
import { ActionSelect, TriggerEdit } from '../controls';
const selectSectionCss = "soam7s";
const SelectSection = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(selectSectionCss, props.className)} />;
});

// TODO: replace with usual button when Workflows is fully SPA
const NextButton = ({
  url,
  onNext,
  validateData
}) => {
  const [valid, setValid] = useState(false);
  const onClick = evt => {
    const dataValid = validateData();
    setValid(dataValid);
    if (dataValid) {
      onNext();
    } else {
      evt.preventDefault();
    }
  };
  return <Button as="a" onClick={onClick} loading={valid} href={url}>
      {I18n.t('publisher.action.next')}
    </Button>;
};
const useActionAllowedValidation = action => {
  const isTrial = useSelector(isOnTrialStageSelector);
  const featureTier = useSelector(state => featureTierSelector(state, {
    featureKey: 'workflows'
  }));
  const isFeatureAvailable = useSelector(state => isAvailableFeatureSelector(state, {
    featureKey: 'workflows'
  }));
  const smsPublisherPaywallFeatureFlagEnabled = useFlag('trust_and_safety_sms_publisher_paywall_feature');
  const shouldBlockSmsToInvitee = action === 'booking.sms.event_to_invitee' && (isTrial || !isFeatureAvailable);
  const shouldBlockSmsToPublisher = action === 'booking.sms.event_to_publisher' && (shouldBlockTrialSMSForPublisher(isTrial, smsPublisherPaywallFeatureFlagEnabled) || !isFeatureAvailable);
  if (shouldBlockSmsToInvitee || shouldBlockSmsToPublisher) {
    const ctaMessage = I18n.t('publisher.workflows.actions.sms.paywall', {
      tier: featureTier
    });
    return HtmlParser(ctaMessage, {
      'Upgrade.Link': UpgradeLink
    });
  }
  return null;
};
const useAnalytics = (triggerType, track) => () => {
  track({
    name: TRACKING_EVENT_NAMES.MODAL_INTERACTION,
    properties: {
      modal_name: 'create_a_workflow',
      action: 'next',
      modal_details: `trigger_${triggerType.split('.').at(-1)}`,
      location: 'intro_flow'
    }
  });
};
export const Wizard = ({
  owner,
  isOpen,
  onClose
}) => {
  const [triggerType, setTriggerType] = useState(null);
  const [triggerError, setTriggerError] = useState();
  const [timeValue, setTimeValue] = useState(null);
  const [timeUnits, setTimeUnits] = useState(null);
  const [action, setAction] = useState(null);
  const [actionError, setActionError] = useState();
  const actionAllowedError = useActionAllowedValidation(action);
  const [secondStepDisabled, setSecondStepDisabled] = useState(true);
  const [timingHasError, setTimingHasError] = useState(false);
  const track = useTrack();
  const allActionError = actionAllowedError || actionError;
  const url = appNewWorkflowUrl(owner);
  const upgradeInProgress = useSelector(upgradeInProgressSelector);
  const submitPath = `${url}?${buildQuery({
    triggerType,
    timeValue,
    timeUnits,
    workflowsAction: action
  })}`;
  useEffect(() => {
    setSecondStepDisabled(!triggerType);
  }, [triggerType]);
  function onTriggerChange(newTriggerType, newTimeValue, newTimeUnits) {
    setTriggerError(undefined);
    setTriggerType(newTriggerType);
    setTimeValue(newTimeValue);
    setTimeUnits(newTimeUnits);
  }
  const onActionChange = newAction => {
    setActionError();
    setAction(newAction);
  };
  const validateData = () => {
    // Exit early if there are any existing form errors
    if (timingHasError || allActionError || triggerError) {
      return false;
    }
    let valid = true;
    if (!action) {
      valid = false;
      setActionError(I18n.t('errors.messages.blank'));
    }
    if (!triggerType) {
      valid = false;
      setTriggerError(I18n.t('errors.messages.blank'));
    }
    return valid;
  };
  const analytics = useAnalytics(triggerType, track);
  return <Popup isOpen={isOpen} onClose={onClose}>
      <Header>
        <Title>
          {I18n.t('publisher.workflows.create.title.user')}
        </Title>
      </Header>
      <Body aria-disabled={upgradeInProgress}>
        <TriggerEdit triggerType={triggerType} timeValue={timeValue} timeUnits={timeUnits} onChange={onTriggerChange} onError={setTimingHasError} selectError={triggerError} selectInvalid={!!triggerError} />
        <SelectSection>
          <ActionSelect disabled={secondStepDisabled} value={action} onChange={onActionChange} error={allActionError} invalid={!!allActionError} />
        </SelectSection>
      </Body>
      <Footer>
        <Button decoration="secondary-outline" onClick={onClose}>
          {I18n.t('publisher.action.cancel')}
        </Button>
        <NextButton onNext={() => analytics()} validateData={validateData} url={submitPath} />
      </Footer>
    </Popup>;
};

require("./wizard.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./wizard.js");